/**
 * LocalCache - IndexedDB-based caching utility for browser-side data persistence
 *
 * This class provides a simple, efficient caching layer using IndexedDB for storing
 * and retrieving data with automatic TTL (Time To Live) expiration. Perfect for
 * caching API responses, filter data, and other frequently accessed information.
 *
 * Features:
 * - Fast asynchronous operations with async/await support
 * - Automatic TTL expiration checking
 * - Stores complex objects (no JSON.stringify needed)
 * - Database connection pooling/caching
 * - Comprehensive error handling
 * - Simple key-value interface
 *
 * @class LocalCache
 * @example
 * // Initialize with 5 minute TTL
 * const cache = new LocalCache({ ttl: 1000 * 60 * 5 });
 *
 * // Store data
 * await cache.setCache('user-profile', { name: 'John', age: 30 });
 *
 * // Retrieve data (auto-deletes if expired)
 * const userData = await cache.getCacheData('user-profile');
 *
 * // Clear specific entry
 * await cache.deleteCache('user-profile');
 *
 * // Clear all cache
 * await cache.clearAllCache();
 */
export class LocalCache {
  /** @type {IDBDatabase|null} Cached database connection instance */
  #db;

  /** @type {string} IndexedDB database name */
  #dbName = 'iminxx';

  /** @type {number} IndexedDB schema version */
  #version = 1;

  /** @type {string} IndexedDB object store name */
  #store = 'cache';

  /** @type {number} Time to live in milliseconds before cache entry expires */
  #TTL = 1000 * 60 * 5; // 5 minutes

  /**
   * Creates a new LocalCache instance
   *
   * @param {Object} options - Configuration options
   * @param {number} options.ttl - Cache time to live in milliseconds
   *   - 1000 * 60 * 5 = 5 minutes
   *   - 1000 * 60 * 10 = 10 minutes
   *   - 1000 * 60 * 60 = 1 hour
   *
   * @example
   * // Create cache with 10 minute TTL
   * const cache = new LocalCache({ ttl: 1000 * 60 * 10 });
   */
  constructor({ ttl }) {
    this.#TTL = ttl;
  }

  /**
   * Opens or retrieves cached IndexedDB database connection
   *
   * Creates the database and object store on first access.
   * Subsequent calls return the cached database instance for performance.
   *
   * Process:
   * 1. Returns cached database if already open
   * 2. Opens IndexedDB connection on first call
   * 3. Creates object store on version upgrade (first time only)
   * 4. Caches connection for reuse
   *
   * @returns {Promise<IDBDatabase>} Promise resolving to the IndexedDB database instance
   * @throws {Error} Throws if database connection fails
   * @private
   *
   * @example
   * const db = await this.#openConnection();
   */
  #openConnection() {
    // Return cached database if already open
    if (this.#db) return this.#db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.#dbName, this.#version);

      request.onerror = (event) => {
        console.error(`Database error: ${event.target.error?.message}`);
        reject(event.target.error);
      };

      request.onsuccess = (event) => {
        const db = event.target.result;
        // Cache the database connection
        this.#db = db;

        // resolve db
        resolve(db);
      };

      // This fires on first open or version upgrade
      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.#store)) {
          const objectStore = db.createObjectStore(this.#store, {
            keyPath: 'key',
          });

          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  /**
   * Retrieves cached data from IndexedDB by key
   *
   * Process:
   * 1. Opens database connection
   * 2. Retrieves data by key
   * 3. Checks if cache has expired based on TTL
   * 4. Auto-deletes expired cache entries
   * 5. Returns null if not found or expired
   *
   * @param {string} cacheKey - The unique cache key to retrieve
   * @returns {Promise<Object|null>} Promise resolving to cached object with structure:
   *   - {key, data, timestamp} if found and valid
   *   - null if not found or expired
   * @throws {Error} Throws if database operation fails
   *
   * @example
   * // Retrieve user data
   * const cached = await cache.getCacheData('user-123');
   * if (cached) {
   *   console.log('Cached data:', cached.data);
   * }
   *
   * @example
   * // Retrieve filter results
   * const filters = await cache.getCacheData('filters-bra-coverage');
   * if (!filters) {
   *   // Fetch fresh data from API
   *   const freshData = await fetchFilters();
   *   await cache.setCache('filters-bra-coverage', freshData);
   * }
   */
  async getCacheData(cacheKey) {
    try {
      const db = await this.#openConnection();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction(this.#store, 'readonly');
        const objectStore = transaction.objectStore(this.#store);
        const request = objectStore.get(cacheKey);

        request.onsuccess = (event) => {
          const result = event.target.result;

          if (!result) {
            resolve(null);
            return;
          }

          // Check if cache is expired (TTL check)
          const now = Date.now();
          const cacheAge = now - result.timestamp;

          if (cacheAge > this.#TTL) {
            // Cache expired, delete it
            this.deleteCache(cacheKey);
            resolve(null);
          } else {
            resolve(result);
          }
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('getCacheData error:', error);
      return null;
    }
  }

  /**
   * Stores data in IndexedDB with automatic timestamp
   *
   * Process:
   * 1. Opens database connection
   * 2. Creates cache object with key, data, and timestamp
   * 3. Stores in IndexedDB using put() method
   * 4. TTL expiration checked automatically on retrieval
   *
   * Note: Data is stored as-is (objects don't need JSON.stringify)
   *
   * @param {string} cacheKey - The unique cache key
   * @param {any} data - Data to cache (objects, arrays, strings, etc.)
   * @returns {Promise<void>} Promise resolving when data is successfully stored
   * @throws {Error} Throws if database operation fails
   *
   * @example
   * // Store complex object
   * await cache.setCache('user-profile', {
   *   id: 123,
   *   name: 'John Doe',
   *   preferences: { theme: 'dark', notifications: true }
   * });
   *
   * @example
   * // Store API response
   * const filters = await fetchFilters();
   * await cache.setCache('filters-bra-coverage', filters);
   *
   * @example
   * // Store array data
   * const products = await fetchProducts();
   * await cache.setCache('products-list', products);
   */
  async setCache(cacheKey, data) {
    try {
      const db = await this.#openConnection();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction(this.#store, 'readwrite');
        const objectStore = transaction.objectStore(this.#store);

        const cacheObject = {
          key: cacheKey,
          data: data,
          timestamp: Date.now(),
        };

        const request = objectStore.put(cacheObject);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('setCache error:', error);
    }
  }

  /**
   * Deletes a specific cache entry by key
   *
   * Use this to manually remove a cache entry before TTL expiration.
   * Expired entries are automatically deleted on retrieval.
   *
   * @param {string} cacheKey - The cache key to delete
   * @returns {Promise<void>} Promise resolving when deletion is complete
   * @throws {Error} Throws if database operation fails
   *
   * @example
   * // Clear user data when they log out
   * await cache.deleteCache('user-profile');
   *
   * @example
   * // Clear outdated filter data
   * await cache.deleteCache('filters-bra-coverage');
   */
  async deleteCache(cacheKey) {
    try {
      const db = await this.#openConnection();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction(this.#store, 'readwrite');
        const objectStore = transaction.objectStore(this.#store);
        const request = objectStore.delete(cacheKey);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('deleteCache error:', error);
    }
  }

  /**
   * Clears all cached data from IndexedDB
   *
   * This removes all cache entries regardless of TTL status.
   * Use for logout, reset, or storage cleanup operations.
   *
   * @returns {Promise<void>} Promise resolving when all cache is cleared
   * @throws {Error} Throws if database operation fails
   *
   * @example
   * // Clear all cache on user logout
   * const cache = new LocalCache({ ttl: 1000 * 60 * 10 });
   * await cache.clearAllCache();
   *
   * @example
   * // Reset cache for debugging
   * await cache.clearAllCache();
   * // Cache is now empty, next requests will fetch fresh data
   */
  async clearAllCache() {
    try {
      const db = await this.#openConnection();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction(this.#store, 'readwrite');
        const objectStore = transaction.objectStore(this.#store);
        const request = objectStore.clear();

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('clearAllCache error:', error);
    }
  }
}

/**
 * FitSearchDynamic is a custom HTML element that manages a multi-step product filtering interface
 * for finding and creating product fits based on various attributes like coverage, padding, etc.
 *
 * This component handles:
 * - Multi-step stepper navigation
 * - Product filtering by metafield attributes
 * - Selection state management
 * - Dynamic product fetching from Shopify GraphQL API
 * - Collection item navigation and filtering
 * - Debounced API calls for performance optimization
 * - Event delegation for dynamically created buttons
 *
 * @class FitSearchDynamic
 * @extends {HTMLElement}
 *
 * @example
 * // In HTML:
 * <fit-search-dynamic>
 *   <stepper-component>...</stepper-component>
 *   <button class="create-fit-button-js">Create Fit</button>
 *   <button class="search-fit-button-js">Find Fit</button>
 *   <!-- More content -->
 * </fit-search-dynamic>
 *
 * // Register the component
 * customElements.define('fit-search-dynamic', FitSearchDynamic);
 */
class FitSearchDynamic extends HTMLElement {
  /** @type {StepperComponent} Reference to the stepper navigation component */
  stepper;

  /** @type {string} Mode of operation: 'static' or 'dynamic' */
  #mode = 'dynamic';

  /** @type {number} Timer ID for debouncing API calls */
  #debounceTimer;

  /** @type {number} Delay in milliseconds for debouncing (default: 300ms) */
  #debounceDelay = 300;

  /** @type {AbortController} Controller for aborting fetch requests */
  #abortController;

  /** @type {boolean} Flag indicating if an API fetch is in progress */
  #isFetching = false;

  /** @type {string} Root collection handle for navigation */
  #rootHandle = 'all';
  /** @type {boolean} Flag for disabling selection that have zero items */
  #disabled_zero_value_selection = false;

  /**@type {number}  Time to live for the cache before refreshing cache data*/
  #TTL = 1000 * 60 * 10; // 10 minutes

  /**
   * When set to glocal, it perfoms query to all products collection filtering all products within the store, when set to scope it only query the declared collection handle provided in the metafield config
   * @type {"global" | "scope"}
   */
  #queryType = 'global';

  /**
   * @type {"root" | "custom"}  Specifies what url to target in Create your Fit selection. Options: `root` or `custom`
   *
   * When targeting `root` this will redirect to root handle with applied product_type filter, whereas for `custom`
   * this will target the metafield `collectionHandle` that is set in `custom.navigation_product_filter_configuration` collection metafield
   */
  #create_fit_url_target = 'root';

  /**@type {boolean}  Flag for animated selection button on screen active state*/
  #animated = false;

  /** @type {Object} Internal state object storing filter selections */
  #states = {
    filters: [],
  };

  /**@type {LocalCache} */
  #cache;

  /**@type {Boolean} */
  #isPending = false;

  #findFitCollectionProductCountMappingKey = 'find:fit:count:mapping';

  /**
   * Constructor for FitSearchDynamic component
   * Initializes the custom element
   *
   * @constructor
   */
  constructor() {
    super();
    this.#queryType = this.dataset.queryType;
  }

  /**
   * Override the default target handle
   * @param {*} handle - collection handle
   * @returns
   */
  setRootHandle(handle) {
    if (!handle) return console.warn('Handle is required!');
    this.#rootHandle = handle;
  }

  /**
   * Initialize by targetting specific collection
   * @param {string} handle - collection handle
   * @param {number} screen - default screen to redirect on open component
   */
  async collectionInit(handle, screen = 2) {
    this.stepper.goToScreen(screen); // navigate to the screen

    // check if existing handle matches the provided handle
    if (handle !== this.#rootHandle) {
      this.setRootHandle(handle);
    }

    await this.fetchFilters();
  }

  /**
   * Lifecycle hook called when the element is inserted into the DOM.
   * Initializes the stepper component reference, sets up event listeners,
   * and fetches initial filter data from the collection.
   *
   * @returns {void}
   */
  connectedCallback() {
    this.stepper = this.querySelector('stepper-component');

    this.setUpListener();

    this.#animated = JSON.parse(this.dataset.animatedSelection || 'false');
    this.#mode = this.dataset.selectionMode;
    this.#rootHandle = this.dataset.rootCollectionHandle || 'all';
    this.#create_fit_url_target =
      this.dataset.createFitTargetCollection || 'root';
    this.#disabled_zero_value_selection =
      this.dataset.disabledZeroValueSelection === 'true' || false;

    if (this.dataset?.selectionCacheTtl) {
      this.#TTL = 1000 * 60 * parseFloat(this.dataset.selectionCacheTtl);
    }
    // initialize cache after the settings applied on initial dom render
    this.#cache = new LocalCache({ ttl: this.#TTL });

    this.#fetchFindFitSelectionCount();
  }

  /**
   * Fetches filter data from the all-products collection and renders
   * selection contents across all screens. Combines collection filters
   * with allowed filter configuration and screen mapping.
   *
   * @returns {Promise<void>}
   * @throws {Error} Throws if filter query fails
   *
   * @example
   * fitSearchElement.fetchFilters();
   */
  async fetchFilters() {
    try {
      this.showLoaderPlaceholder();
      if (this.#isPending) return;
      this.#isPending = true;
      const cacheKey = `${this.#rootHandle}`;
      const cachedData = await this.#cache.getCacheData(cacheKey);
      let data;

      if (cachedData) {
        data = cachedData.data;
      } else {
        data = await this.#performFilterQuery(
          `/collections/${this.#rootHandle}`,
        );

        this.#cache.setCache(cacheKey, data);
      }

      if (data) {
        const allowedFilters = this.parseAllowedFilters();
        const screenMapping = this.parseScreenMapping();
        const findFitMapping = await this.#fetchFindFitSelectionCount();

        this.clearSelectionContents();

        this.renderSelectionContents(
          data,
          allowedFilters,
          screenMapping,
          findFitMapping,
        );
      }
    } catch (e) {
      console.warn('Fit Component Error: ' + e);
    } finally {
      this.hideLoaderPlaceholder();
      this.#isPending = false;
    }
  }

  /**
   * Fetches the collection products count and set it to the cache
   * @returns
   */
  async #fetchFindFitSelectionCount() {
    const cachedData = await this.#cache.getCacheData(
      this.#findFitCollectionProductCountMappingKey,
    );

    if (cachedData) {
      return cachedData.data;
    }

    const allowedFilters = this.parseAllowedFilters();

    const productTypes = allowedFilters.product_type || [];

    const results = await Promise.all(
      productTypes.map(async (productType) => {
        const handle = productType.collectionHandle;

        const count = await this.#queryCollectionProductsCount(
          `/collections/${handle}`,
          true,
        );

        return {
          [productType.label]: {
            ...productType,
            count,
          },
        };
      }),
    );

    const resultsMap = Object.assign({}, ...results);

    await this.#cache.setCache(
      this.#findFitCollectionProductCountMappingKey,
      resultsMap,
    );

    return resultsMap;
  }

  /**
   * Creates a unique cache key based on current filter selections.
   * The key is generated from the serialized filter state, making it unique
   * to each combination of selected options. This allows different filter
   * combinations to be cached independently.
   *
   * Cache key format: JSON stringified array of "value:screen" pairs joined by colons
   *
   * @returns {string} Unique cache key based on current filter selections
   *
   * @example
   * // With filters: [{value: 'full', screen: 1}, {value: 'padded', screen: 2}]
   * const key = element.createCacheKey();
   * // Returns: "[\"full:1:padded:2\"]"
   */
  createCacheKey() {
    return JSON.stringify(
      this.#states.filters
        .map((filter) => `${filter.value}:${filter.screen}`)
        .join(':'),
    );
  }

  /**
   * Performs an asynchronous filter query by fetching a collection page
   * and extracting filter data from embedded JSON script tags.
   *
   * This method:
   * - Fetches the HTML page from the specified URL
   * - Parses the HTML to find the collection-filters-data script
   * - Extracts and returns the filters array
   *
   * @param {string} url - The collection URL to fetch filters from
   * @returns {Promise<Array>} Promise resolving to array of filter objects with structure:
   *   - {label, param_name, type, values: [{label, value, count}, ...]}
   * @throws {Error} Throws if fetch or parsing fails
   *
   * @example
   * const filters = await element.#performFilterQuery('/collections/all-products?filter.p.product_type=Bra');
   * @private
   */
  async #performFilterQuery(url) {
    try {
      if (this.#abortController) {
        this.#abortController.abort();
      }

      this.#abortController = new AbortController();

      const data = await fetch(url, {
        signal: this.#abortController.signal,
      })
        .then((response) => response.text())
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const filterScript = doc.getElementById('collection-filters-data');

          if (filterScript) {
            const data = JSON.parse(filterScript.textContent);
            const filters = data.filters;

            if (filters) {
              return filters;
            }
            return [];
          }
          return [];
        });

      return data;
    } catch (e) {
      throw e;
    }
  }

  async #queryCollectionProductsCount(url) {
    try {
      const data = await fetch(url)
        .then((response) => response.text())
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const filterScript = doc.getElementById('collection-filters-data');

          if (filterScript) {
            const data = JSON.parse(filterScript.textContent);
            const count = data.products_count;

            return count;
          }
          return 0;
        });

      return data;
    } catch (e) {
      throw e;
    }
  }

  /**
   * Parses screen-to-filter mapping configuration from a JSON script element.
   * This mapping defines which filter corresponds to which screen and what
   * metafield properties should be used.
   *
   * @returns {Object} Parsed screen mapping object with structure:
   *   - {[screenKey]: {filter, metafield, selector, ...}}
   * @returns {Object} Empty object if script element is not found
   *
   * @example
   * // HTML: <script id="screen_filter_mapping">{"screen_1": {filter: "filter.p.coverage_type", ...}}</script>
   * const mapping = element.parseScreenMapping();
   */
  parseScreenMapping() {
    const screenMapping = document.getElementById(
      'screen_filter_mapping',
    ).textContent;

    if (screenMapping) {
      const parsedScreenMapping = JSON.parse(screenMapping);

      return parsedScreenMapping;
    }

    return {};
  }

  /**
   * Parses allowed filter configuration from a JSON script element.
   * Defines which product type selections should be displayed with their
   * metadata like images, labels, and constraints.
   *
   * @returns {Object} Parsed allowed filters object with structure:
   *   - {[metafieldName]: [{label, image, selectionValue, constraint?, ...}, ...]}
   * @returns {Object} Empty object if script element is not found
   *
   * @example
   * // HTML: <script id="collection_allowed_filters">{"coverage_type": [{label: "Full", image: "...", ...}]}</script>
   * const allowedFilters = element.parseAllowedFilters();
   */
  parseAllowedFilters() {
    const allowed_filter = document.getElementById(
      'collection_allowed_filters',
    ).textContent;

    if (allowed_filter) {
      const parsedAllowedFilters = JSON.parse(allowed_filter);

      return parsedAllowedFilters;
    }

    return {};
  }

  /**
   * Constructs URL query parameters from the current filter state.
   * Converts internal filter state array into a URLSearchParams string.
   *
   * @returns {string} URL-encoded query parameter string
   *   Format: "filter.p.key1=value1&filter.p.key2=value2"
   *
   * @example
   * const params = element.constructFilterParams();
   * console.log(params); // "filter.p.product_type=Bra&filter.p.coverage_type=Full"
   */
  constructFilterParams() {
    const params = new URLSearchParams();

    this.#states.filters.forEach((filter) => {
      // when query Type is set to scope we ignore product_type query since it messed up the quantity because it will apply product_type filter on selection click
      if (
        this.#queryType === 'scope' &&
        filter.key === 'filter.p.product_type'
      ) {
        return; // skip this filter
      }

      params.set(filter.key, filter.value);
    });

    return params.toString();
  }

  /**
   * Renders selection buttons for each screen based on filtered data, allowed filters,
   * and screen mapping configuration.
   *
   * Process:
   * 1. Iterates through screen mapping to identify relevant filters
   * 2. Maps collection filter values with allowed filter metadata
   * 3. Creates button elements (link or button mode) for each selection
   * 4. Inserts buttons before existing static content
   * 5. Applies constraints to hide options not applicable to current screen
   *
   * @param {Array<Object>} filters - Collection filter objects from Shopify
   * @param {Object} allowedFilters - Metafield configuration defining allowed selections
   * @param {Object} screenMapping - Mapping of screens to filter configurations
   * @param {Object} findFitMapping - Mapping of selector for find fit displaying the total product count inside of the target collection
   * @returns {void}
   *
   * @example
   * renderSelectionContents(
   *   filters,
   *   { coverage_type: [{label: 'Full', image: '...', selectionValue: 'full'}] },
   *   { coverage_screen: {filter: 'filter.p.coverage_type', metafield: 'coverage_type', ...} }
   * );
   */
  renderSelectionContents(
    filters,
    allowedFilters = {},
    screenMapping = {},
    findFitMapping = {},
  ) {
    Object.entries(screenMapping).forEach(([key, obj]) => {
      const filter = filters.find((fil) => fil.param_name === obj.filter);
      const metafield = obj.metafield;

      if (filter) {
        const selections = filter.values;

        const selectionFilter = allowedFilters[metafield] || [];

        const selectionMap = new Map(selections.map((s) => [s.label, s]));

        const allowedSelections = selectionFilter.map((allowedFil) => {
          const matchingSelection = selectionMap.get(allowedFil.selectionValue);
          const { label, ...rest } = allowedFil;

          return {
            ...rest,
            value: matchingSelection?.value,
            count: matchingSelection?.count || 0,
            label: label,
          };
        });

        const screenElement = this.querySelectorAll(obj.selector);

        screenElement.forEach((el) => {
          const isFindFit = el.dataset?.isFindFit === 'true';
          const mode = el.dataset?.mode;
          const screenId = el.dataset.screenId || '';
          const nextScreenIndex = el.dataset.nextScreenIndex;
          const nextScreenIndexes = el.dataset.nextScreenIndexes;

          el.setAttribute('data-filter-reference', obj.filter);

          const screenElementContentContainer = el.querySelector(
            '#collections-results-list',
          );

          const firstExistingChild =
            screenElementContentContainer.firstElementChild;

          allowedSelections.map((selection, index) => {
            let selector;
            let indexes = [];

            if (nextScreenIndexes) {
              indexes = JSON.parse(nextScreenIndexes);
            }

            if (
              selection?.constraint &&
              screenId &&
              !screenId.includes(selection.constraint.toLowerCase())
            )
              return null;

            const elNextscreenIndex = indexes[index] || nextScreenIndex;

            // Find Fit Selection Updates
            if (mode === 'link') {
              let url;
              const searchParams = new URLSearchParams();
              if (
                selection?.collectionHandle &&
                this.#create_fit_url_target === 'custom'
              ) {
                url = `/collections/${selection.collectionHandle}`;
              } else {
                // set the filter params since this will be used as product_type filter
                searchParams.set(
                  obj.filter,
                  selection.value || selection.selectionValue,
                );

                url = `/collections/${this.#rootHandle}`;
              }

              // add expanded params for filter drawer expanded state
              searchParams.set('filter_expanded', true);

              selector = document.createElement('a');
              selector.href = url + '?' + searchParams.toString();
              selector.className = 'w-full no-underline selection-item-link-js';

              let count = 0;

              if (findFitMapping[selection.label]?.count) {
                count = findFitMapping[selection.label].count;
              }

              // Disabled
              if (this.#disabled_zero_value_selection && count <= 0) {
                selector.style.pointerEvents = 'none';
                selector.style.opacity = '0.6';
                selector.classList.add('disabled');
              }

              selector.innerHTML = `
              <button
                data-collection-id=""
                data-selection-value="${
                  selection.value || selection.selectionValue
                }"
                data-animated-selection="${this.#animated}"
                class="collection-item-js w-full btn-reset no-underline flex align-center gap-10 overflow-hidden hover-grey-bg block"
              >
                <div
                  class="bg-[rgba(0,0,0,0.04)] overflow-hidden collections-img-wrapper"
                  style="--width:60px;"
                >
                  <img src="${selection.image}" alt="${selection.label}" width="640" height="640" loading="lazy" class="collections-img" />
                </div>

                <div class="px-12 py-10">
                  <div class="text-base fs-small truncate hover-heading-color">
                    ${selection.label} (${count})
                  </div>
                </div>
              </button>
              `;
            } else {
              const back_image = selection?.back_image;

              let count = selection.count;

              // only override the count in the first screen(Find your fit) at index 0
              // this will sync the initial count for bot Create/Find your fit
              // override initial count when set to scope rendering products count with the specific collection
              if (
                !isNaN(findFitMapping[selection.label]?.count) &&
                this.#queryType === 'scope' &&
                isFindFit
              ) {
                count = findFitMapping[selection.label].count;
              }

              selector = document.createElement('button');
              selector.className = `selection-item-js btn-reset no-underline flex align-center gap-10 overflow-hidden hover-grey-bg block ${
                back_image ? 'alternate-preview' : ''
              }`;
              selector.setAttribute(
                'data-selection-value',
                selection.value || selection.selectionValue,
              );
              selector.setAttribute('data-label', selection.label);
              selector.setAttribute('data-animated-selection', this.#animated);
              selector.setAttribute('data-selection-filter', obj.filter);
              selector.setAttribute('data-collection-id', '');
              selector.setAttribute(
                'data-collection-handle',
                selection.collectionHandle,
              );
              selector.setAttribute(
                'data-next-screen-index',
                elNextscreenIndex,
              );
              selector.setAttribute('data-selection-count', count);

              if (this.#disabled_zero_value_selection && count <= 0) {
                selector.disabled = true;
              }

              selector.innerHTML = `
                 <div class="flex-1 flex gap-10 align-center">
                  <div class="bg-[rgba(0,0,0,0.04)] overflow-hidden collections-img-wrapper" style="--width:60px;">
                    <img 
                      src="${selection.image}" 
                      alt="${selection.label}" 
                      width="640" 
                      height="640" 
                      loading="lazy"
                      class="collections-img" 
                    />
                     <img
                        src="${back_image}"
                        alt="${selection.label}"
                        width="640"
                        height="640"
                        loading="lazy"
                        class="collections-img back-preview"
                      />
                  </div>
                  <div class="px-12 py-10">
                    <div class="text-base fs-small truncate hover-heading-color">
                      ${selection.label} (${count})
                    </div>
                  </div>
                 </div>
                  <div class="selection-loader animate-spin flex items-center p-4">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" class="spin icon-load">
                      <use href="#icon-load"></use>
                    </svg>
                  </div>
              `;
            }

            if (firstExistingChild) {
              firstExistingChild.insertAdjacentElement('beforebegin', selector);
            } else {
              screenElementContentContainer.appendChild(selector);
            }
          });
        });
      }
    });
  }

  /**
   * Clears the existing selection contents
   */
  clearSelectionContents() {
    const collectionListScreens = this.querySelectorAll(
      '#collections-results-list',
    );

    collectionListScreens.forEach((collectionScreen) => {
      const selections =
        collectionScreen.querySelectorAll('.selection-item-js');
      const selectionLinks = collectionScreen.querySelectorAll(
        '.selection-item-link-js',
      );

      selectionLinks.forEach((link) => link.remove());

      selections.forEach((selection) => {
        selection.remove();
      });
    });
  }

  /**
   * Displays a loading indicator on the selection button.
   * Adds 'loading' class and disables the button to prevent multiple clicks.
   *
   * @param {HTMLElement} selectionEl - The button element to show loader on
   * @returns {void}
   *
   * @example
   * showLoader(buttonElement);
   */
  showLoader(selectionEl) {
    if (!selectionEl) return;
    selectionEl.classList.add('loading');
    selectionEl.disabled = true;
  }

  /**
   * Hides the loading indicator on the selection button.
   * Removes 'loading' class and re-enables the button.
   *
   * @param {HTMLElement} selectionEl - The button element to hide loader on
   * @returns {void}
   *
   * @example
   * hideLoader(buttonElement);
   */
  hideLoader(selectionEl) {
    if (!selectionEl) return;
    selectionEl.classList.remove('loading');
    selectionEl.disabled = false;
  }

  /**
   * Adds or updates a filter in the component state for product filtering.
   * If a filter with the same key already exists, it updates the existing filter;
   * otherwise, it adds a new filter.
   *
   * @param {Object} filter - The filter object to set
   * @param {string} filter.key - The filter key (typically a metafield name or param_name)
   * @param {string} filter.value - The filter value to match
   * @returns {void}
   *
   * @example
   * fitSearchElement.setFilterState({
   *   key: 'filter.p.coverage_type',
   *   value: 'full_coverage'
   * });
   */
  setFilterState(filter) {
    const filterIndex = this.#states.filters.findIndex(
      (fil) => fil.key === filter.key,
    );

    if (filterIndex !== -1) {
      this.#states.filters[filterIndex] = filter; // update the filter
    } else {
      this.#states.filters.push(filter);
    }
  }

  /**
   * Removes a filter from the component state by its key.
   * Clears all filters from the specified key onwards.
   *
   * @param {string} key - The filter key to remove
   * @returns {void}
   *
   * @example
   * fitSearchElement.removeFilterState('filter.p.coverage_type');
   */
  removeFilterState(key) {
    const filterIndex = this.#states.filters.findIndex(
      (fil) => fil.key === key,
    );

    if (filterIndex !== -1) {
      this.#states.filters = this.#states.filters.slice(0, filterIndex);
    }
  }

  /**
   * Clears all filters from the component state.
   * Resets the filter array to empty, allowing a fresh selection flow.
   *
   * @returns {void}
   *
   * @example
   * fitSearchElement.clearFilterState();
   */
  clearFilterState() {
    this.#states.filters = [];
  }

  /**
   * Removes a filter associated with a specific screen index.
   * Ensures that when a user navigates back to a previous screen,
   * the filter selections for that screen are reset.
   *
   * @param {number} screen - The screen index to remove filters for
   * @returns {void}
   *
   * @example
   * fitSearchElement.removeScreenFilterState(2);
   */
  removeScreenFilterState(screen) {
    const filterIndex = this.#states.filters.findIndex(
      (fil) => fil.screen === screen,
    );

    if (filterIndex !== -1) {
      this.#states.filters = this.#states.filters.slice(0, filterIndex);
    }
  }

  /**
   * Sets up all event listeners for the component including:
   * - Create fit button - navigates to creation flow
   * - Find fit button - navigates to search flow
   * - Return/navigation buttons - handles backward navigation
   * - Selection buttons - uses event delegation for dynamically added buttons
   * - Screen update events - handles filter state cleanup on screen change
   *
   * Uses event delegation for selection buttons to support dynamically
   * created button elements without needing to re-attach listeners.
   *
   * @returns {void}
   * @private
   */
  setUpListener() {
    // Set up create fit button listener
    const createFitButton = this.querySelector('.create-fit-button-js');

    createFitButton.addEventListener('click', () => {
      this.stepper.goToScreen(2);
    });

    // Set up find fit button listener
    const findFitButton = this.querySelector('.search-fit-button-js');

    findFitButton.addEventListener('click', () => {
      this.stepper.setScreen(1);
    });

    // Set up return button listener
    const returnButtons = this.querySelectorAll('.navigation__return-js');
    returnButtons.forEach((btn) => {
      btn.addEventListener('click', () => this.listenReturnButtonClick(btn));
    });

    // Event delegation for selection buttons
    const screenElementContentContainer = this.querySelectorAll(
      '#collections-results-list',
    );

    screenElementContentContainer.forEach((screenEl) => {
      // Single listener on parent using event delegation
      screenEl.addEventListener('click', (e) => {
        const selectionButton = e.target.closest('.selection-item-js');
        if (selectionButton) {
          e.preventDefault();
          this.listenSelectionClick(selectionButton);
        }
      });
    });

    /**
     * Custom event listener that listens for screen:update event triggered by breadcrumb clicks
     * Removes filter state associated with the updated screen to reset selections
     */
    this.addEventListener('screen:update', (e) => {
      const currentScreenIndex = Number(e.detail.currentScreenIndex);
      if (isNaN(currentScreenIndex)) return;

      this.removeScreenFilterState(currentScreenIndex + 1);
    });
  }

  /**
   * Retrieves filter data from the current selection with caching logic avoiding query each time the same selection was clickeds.
   *
   * Process:
   * 1. Generates cache key based on current filter selections
   * 2. Attempts to retrieve cached data
   * 3. Returns cached data if valid and not expired (within TTL)
   * 4. If cache is invalid/expired: fetches fresh filter data from collection
   * 5. Creates new cache entry with timestamp and data
   * 6. Stores cache and returns fresh data
   *
   * @returns {Promise<Array>} Promise resolving to array of filter objects with structure:
   *   - {label, param_name, type, values: [{label, value, count}, ...]}
   * @throws {Error} Throws if filter query fails
   *
   * @example
   * // Returns cached data if available and valid
   * const filters = await element.getFilterData();
   *
   * @example
   * // Fetches fresh data if cache expired or doesn't exist
   * const filters = await element.getFilterData();
   */
  async getFilterData() {
    // create cache key for active selections
    const cacheKey = this.createCacheKey();

    // retrieved cached data
    const cached = await this.#cache.getCacheData(cacheKey);

    // check if cached is valid
    if (cached) {
      return cached.data;
    } else {
      // fetch query with current filters applied
      const data = await this.#performFilterQuery(
        `/collections/${this.#rootHandle}?${this.constructFilterParams()}`,
      );

      // save the cache data
      await this.#cache.setCache(cacheKey, data);

      return data;
    }
  }
  /**
   * Handles selection button click events with debouncing and loading states.
   *
   * Process:
   * 1. Shows loading indicator on button
   * 2. Debounces API calls to prevent rapid successive requests
   * 3. Sets filter state for the selected option
   * 4. For 'static' mode: navigates to next screen immediately
   * 5. For 'dynamic' mode: fetches updated filter data and validates next screen availability
   * 6. Hides loader and handles errors
   *
   * @param {HTMLElement} selectionEl - The clicked selection button element
   * @returns {Promise<void>}
   *
   * @example
   * // Called automatically via event delegation
   * element.listenSelectionClick(buttonElement);
   */
  async listenSelectionClick(selectionEl) {
    if (this.#isFetching) return;

    clearTimeout(this.#debounceTimer);
    this.showLoader(selectionEl);

    const collectionHandle = selectionEl.dataset.collectionHandle;

    // handle is updated in every selection that has collectionHandle dataset
    if (collectionHandle !== 'undefined' && this.#queryType === 'scope') {
      this.#rootHandle = collectionHandle;
    }

    this.#debounceTimer = setTimeout(async () => {
      try {
        const nextScreenIndex = Number(selectionEl.dataset.nextScreenIndex);
        const selectionValue = selectionEl.dataset.selectionValue;
        const selectionFilter = selectionEl.dataset.selectionFilter;

        // set the filter
        this.setFilterState({
          key: selectionFilter,
          value: selectionValue,
          screen: nextScreenIndex,
        });

        if (this.#mode === 'static') {
          // Static mode: no API call needed
          if (isNaN(Number(nextScreenIndex)))
            return this.redirectToRootHandle();

          this.stepper.goToScreen(nextScreenIndex);
        } else {
          // Dynamic mode: fetch updated filter data
          if (isNaN(Number(nextScreenIndex)))
            return this.redirectToRootHandle();

          this.#isFetching = true;

          const filterData = await this.getFilterData();

          // update the next screen handle count
          this.updateScreenHandleCount(
            nextScreenIndex,
            filterData,
            (isAvailable) => {
              if (isAvailable) {
                this.stepper.goToScreen(nextScreenIndex);
              } else {
                // redirect to gallery page with filtering applied
                this.redirectToRootHandle();
              }
            },
          );
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Request cancelled');
          return;
        }
        window?.toast(error?.message, 'error');
      } finally {
        this.#isFetching = false;
        this.hideLoader(selectionEl);
      }
    }, this.#debounceDelay);
  }

  /**
   * Handles the return/navigation button click event.
   *
   * Behavior:
   * - If return index is specified: clears all filters and navigates to that screen
   * - Otherwise: removes filter for current screen and goes to previous screen
   *
   * @param {HTMLElement} element - The return button element
   * @returns {void}
   * @private
   *
   * @example
   * listenReturnButtonClick(returnButton);
   */
  listenReturnButtonClick(element) {
    const screenReturnIndex = element.dataset.screenReturnIndex;

    if (screenReturnIndex && !isNaN(Number(screenReturnIndex))) {
      // User navigates back to root selection
      // Clear all filter states to reset the flow
      this.clearFilterState();

      // Navigate to the root screen
      return this.stepper.goToScreen(screenReturnIndex);
    }

    // Remove filter associated with the current screen only
    this.removeScreenFilterState(this.stepper.currentIndex);

    // Navigate to previous screen
    this.stepper.prevScreen();
  }

  /**
   * Redirects the browser to a specified path or URL using window.location.replace().
   *
   * @param {string} path - The destination path or URL to redirect to
   * @returns {any} Result of window.location.replace()
   *
   * @example
   * fitSearchElement.redirectTo('/products?filter=coverage_type%3Dfull');
   */
  redirectTo(path) {
    return window.location.replace(path);
  }

  /**
   * Redirects to the root collection with current filter parameters applied.
   * Constructs URL using root handle and current filter state.
   *
   * @returns {any} Result of window.location.replace()
   *
   * @example
   * fitSearchElement.redirectToRootHandle();
   * // Redirects to: /collections/all-products?filter.p.product_type=Bra&filter.p.coverage_type=Full
   */
  redirectToRootHandle() {
    return this.redirectTo(
      `/collections/${this.#rootHandle}?${this.constructFilterParams()}`,
    );
  }

  /**
   * Updates the display of screen options based on filtered product counts.
   *
   * Process:
   * 1. Finds the next screen element by index
   * 2. Gets the filter reference for that screen
   * 3. Matches options with updated count data
   * 4. Updates UI with new counts using requestAnimationFrame
   * 5. Tracks if any options have products available
   * 6. Executes callback with availability status
   *
   * @param {number} screenIndex - The screen index containing options to update
   * @param {Array<Object>} filterData - The filters data returned from the query
   * @param {(isAvailable: boolean) => void} updateStateCallback - Callback executed with availability status.
   *   If true: screen has available selections. If false: redirect to collection page.
   * @returns {void}
   * @throws {Error} Throws if the screen element with specified index is not found
   *
   * @example
   * fitSearchElement.updateScreenHandleCount(3, filterData, (available) => {
   *   if (available) {
   *     stepper.goToScreen(3);
   *   } else {
   *     redirectToCollection();
   *   }
   * });
   * @private
   */
  updateScreenHandleCount(screenIndex, filterData = [], updateStateCallback) {
    const nextScreen = this.querySelector(
      `[data-screen-index="${screenIndex}"]`,
    );

    if (!nextScreen)
      throw new Error(
        'Unable to get the next screen with index of ' + screenIndex,
      );

    const screenOptions = nextScreen.querySelectorAll(
      '#collections-results-list [data-collection-id]',
    );
    const screenFilterRef = nextScreen.dataset?.filterReference;

    const selectedFilterData = filterData.find(
      (d) => d.param_name === screenFilterRef,
    );

    if (!selectedFilterData) return this.redirectToRootHandle();

    const updatedCountValues = selectedFilterData?.values || [];

    // Batch DOM updates
    const updates = [];

    // flag for next step availability
    let isAvailableNextPage = false;

    screenOptions.forEach((option) => {
      const optionValue = option.dataset.selectionValue;

      // find associated updates
      const newUpdates = updatedCountValues.find(
        (newUpdates) => newUpdates.label === optionValue,
      );

      if (newUpdates) {
        const count = newUpdates.count;

        if (count > 0) {
          isAvailableNextPage = true;
        }

        updates.push({ option, count: count, hasItems: count > 0 });
      }
    });

    if (updates.length > 0) {
      // Apply all updates in one repaint cycle using requestAnimationFrame
      requestAnimationFrame(() => {
        updates.forEach(({ option, count, hasItems }) => {
          // disable zero values
          if (this.#disabled_zero_value_selection && !hasItems) {
            option.disabled = true;
          } else {
            option.removeAttribute('disabled');
          }

          const textField = option.querySelector('.text-base');
          textField.textContent = `${option.dataset.label} (${count})`;
        });
      });
    }

    // runs the callback with availability status
    updateStateCallback(isAvailableNextPage);
  }

  /**
   * Renders a loading skeleton component
   * @returns
   */
  showLoaderPlaceholder() {
    const containers = this.querySelectorAll('.top_search-fit-category');

    containers.forEach((container) => {
      const mainContent = container.querySelector('#collections-results-list');

      if (mainContent) {
        // hide main content grid
        mainContent.style.display = 'none';
      }

      const collectionLoader = container.querySelector(
        '#collections-results-loader',
      );

      if (collectionLoader) {
        collectionLoader.style.display = 'grid';
      }
    });
  }

  /**
   * Hide the skeleton loaders
   */
  hideLoaderPlaceholder() {
    const containers = this.querySelectorAll('.top_search-fit-category');

    containers.forEach((container) => {
      const mainContent = container.querySelector('#collections-results-list');

      if (mainContent) {
        // hide main content grid
        mainContent.style.display = 'grid';
      }

      const collectionLoader = container.querySelector(
        '#collections-results-loader',
      );

      if (collectionLoader) {
        collectionLoader.style.display = 'none';
      }
    });
  }

  /**
   * Lifecycle hook called when the element is removed from the DOM.
   * Performs cleanup to prevent memory leaks:
   * - Clears debounce timers
   * - Aborts any pending fetch requests
   *
   * @returns {void}
   */
  disconnectedCallback() {
    // Cleanup on component removal
    clearTimeout(this.#debounceTimer);

    if (this.#abortController) {
      this.#abortController.abort();
    }
  }
}

customElements.define('fit-search-dynamic', FitSearchDynamic);

/**
 * FitBreadcrumb Web Component
 *
 * A custom HTML element that renders a breadcrumb navigation component.
 * Displays a hierarchical path with the first item as a heading and subsequent
 * items as clickable buttons that trigger navigation events.
 *
 * @extends {HTMLElement}
 *
 * @example
 * // Basic usage in HTML
 * <stepper-component>
 *   <fit-breadcrumb>
 *     <div class="screen-breadcrumb" data-path="Home/Account/Settings"></div>
 *   </fit-breadcrumb>
 * </stepper-component>
 *
 * @example
 * // Listen for navigation events
 * const stepper = document.querySelector('stepper-component');
 * stepper.addEventListener('stepper:skip', (e) => {
 *   console.log('Skip to index:', e.detail.skipIndex);
 * });
 */
class FitBreadcrumb extends HTMLElement {
  /**
   * Creates an instance of FitBreadcrumb
   */
  constructor() {
    super();
  }

  /**
   * Lifecycle hook called when the element is inserted into the DOM.
   * Initializes the breadcrumb component.
   */
  connectedCallback() {
    this.init();
  }

  /**
   * Initializes the breadcrumb component.
   *
   * Retrieves the breadcrumb container element and parses the path data
   * attribute. If a valid path is found, it renders the breadcrumb content.
   *
   * Expected HTML structure:
   * ```html
   * <fit-breadcrumb>
   *   <div class="screen-breadcrumb" data-path="Item1/Item2/Item3"></div>
   * </fit-breadcrumb>
   * ```
   *
   * @returns {void}
   */
  init() {
    this.container = this.querySelector('.screen-breadcrumb');
    this.stepper = this.closest('stepper-component');
    this.screenComponent = this.closest('fit-search-dynamic');

    if (!this.container) return;

    const path = this.container.dataset.path;
    if (path) {
      const pathArr = path.split('/');
      this.renderContent(pathArr);
    }
  }

  /**
   * Renders the breadcrumbs content
   *
   * Generates breadcrumb items from the provided path array:
   * - First item is rendered as an `<h5>` heading
   * - Subsequent items are rendered as clickable `<span>` buttons
   * - Separators are added between items (except after the last item)
   * - Each button dispatches a 'stepper:skip' event when clicked
   *
   * @param {string[]} pathArray - Array of breadcrumb labels split from the path
   *                               (e.g., ['Home', 'Account', 'Settings'])
   *
   * @example
   * breadcrumb.renderContent(['Dashboard', 'Users', 'Profile']);
   * // Renders: Dashboard | Users | Profile
   * // (Dashboard as heading, Users and Profile as buttons)
   */
  renderContent(pathArray = []) {
    for (let i = 0; i < pathArray.length; i++) {
      if (i === 0) {
        const heading = document.createElement('h5');
        heading.innerHTML = [pathArray[i]];
        this.container.appendChild(heading);
        if (pathArray.length > 1) {
          const separator = this.createSeparator();
          this.container.appendChild(separator);
        }
      } else {
        const screenSkipIndex = -i;
        const btn = document.createElement('span');
        btn.role = 'button';
        btn.className = 'screen-breadcrumb-js';
        btn.setAttribute('data-screen-skip-index', screenSkipIndex);
        btn.textContent = pathArray[i];
        btn.addEventListener('click', (e) => {
          e.preventDefault();

          this.stepper.dispatchEvent(
            new CustomEvent('stepper:skip', {
              detail: { skipIndex: screenSkipIndex },
            }),
          );

          // dispatch custom event in root component fit-search-dynamic
          this.screenComponent.dispatchEvent(
            new CustomEvent('screen:update', {
              detail: { currentScreenIndex: this.stepper.currentIndex },
            }),
          );
        });
        this.container.appendChild(btn);

        if (i !== pathArray.length - 1) {
          const separator = this.createSeparator();
          this.container.appendChild(separator);
        }
      }
    }
  }

  /**
   * Creates a separator element
   *
   * Generates a visual separator div used between breadcrumb items.
   * The separator can be styled via CSS using the 'screen-breadcrumb-separator' class.
   *
   * @returns {HTMLDivElement} A div element with the 'screen-breadcrumb-separator' class
   *
   * @example
   * const separator = breadcrumb.createSeparator();
   * // Returns: <div class="screen-breadcrumb-separator"></div>
   */
  createSeparator() {
    const separator = document.createElement('div');
    separator.className = 'screen-breadcrumb-separator';
    return separator;
  }
}

customElements.define('fit-breadcrumb', FitBreadcrumb);
